/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { useEffect, useState, useRef } from 'react'

const chatButtonStyles = css`
  position: fixed;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #2975ba;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  z-index: 9999;
  transition: transform 0.3s ease;
  opacity: 0.7;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  transform: translateZ(0);
  bottom: 20px;
  right: 20px;
  
  &:hover {
    transform: scale(1.1);
  }
  
  &.active {
    transform: rotate(45deg) scale(0.9);
  }
  
  svg {
    width: 24px;
    height: 24px;
    color: white;
  }
  
  img {
    width: 28px;
    height: 28px;
  }
`

const tooltipStyles = css`
  position: fixed;
  background-color: #EFF6FF;
  color: #1E40AF;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 15px;
  max-width: 200px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 9998;
  transition: opacity 0.3s;
  right: 70px;
  bottom: 35px;
  transform: translateY(-50%);
  
  &:after {
    content: '';
    position: absolute;
    width: 8px;
    height: 8px;
    background-color: #EFF6FF;
    transform: rotate(45deg);
    right: -4px;
    top: 50%;
    margin-top: -4px;
  }
  
  .close-btn {
    position: absolute;
    top: 4px;
    right: 4px;
    cursor: pointer;
    font-size: 12px;
    line-height: 1;
    opacity: 0.6;
    padding: 3px;
    background: none;
    border: none;
    color: #1E40AF;
  }
`

const chatContainerStyles = css`
  position: fixed;
  display: flex;
  flex-direction: column;
  width: 0;
  height: 0;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: -1;
  max-width: 100%;
  box-shadow: 0 5px 30px rgba(0, 0, 0, 0.15);
  background-color: #ffffff;
  overflow: hidden;
  transform: translateZ(0);
  pointer-events: none;
  
  &.open {
    opacity: 1;
    z-index: 10000;
    pointer-events: auto;
    width: 800px;
    height: 600px;
    right: 20px;
    bottom: 100px;
    border-radius: 8px;
  }
  
  @media (max-width: 768px) {
    &.open {
      top: 0;
      left: 0;
      width: 100% !important;
      height: 100% !important;
      right: 0;
      bottom: 0;
      border-radius: 0;
    }
  }
`

const chatHeaderStyles = css`
  padding: 12px 16px;
  background-color: white;
  color: #333333;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e0e0e0;
`

const headerTitleStyles = css`
  display: flex;
  align-items: center;
  font-weight: 500;
  
  img {
    width: 24px;
    height: 24px;
    margin-right: 8px;
  }
`

const headerButtonsStyles = css`
  display: flex;
  align-items: center;
  
  button {
    background: none;
    border: none;
    color: #333333;
    cursor: pointer;
    padding: 8px;
    margin-left: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 4px;
    
    &:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }
    
    i {
      font-size: 22px;
    }
  }
`

const loadingContainerStyles = css`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background-color: #f5f5f5;
  
  .loading-spinner {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 3px solid rgba(0, 0, 0, 0.1);
    border-top-color: #2975ba;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  p {
    margin-top: 15px;
    color: #333;
    font-weight: 400;
    text-align: center;
  }
`

const chatFrameStyles = css`
  border: none;
  width: 100%;
  flex: 1;
  display: none;
  
  &.loaded {
    display: block;
  }
`

interface ChatWidgetConfig {
  theme: {
    button: {
      size: number
      backgroundColor: string
      iconColor: string
      opacity: number
      bottom: number
      right: number
      customIconSrc?: string
      customIconSize?: number
      autoWindowOpen?: {
        autoOpen: boolean
        openDelay: number
      }
    }
    tooltip?: {
      showTooltip: boolean
      tooltipMessage: string
      tooltipBackgroundColor: string
      tooltipTextColor: string
      tooltipFontSize: number
    }
    chatWindow: {
      chatFramePath?: string
      showTitle?: boolean
      title: string
      titleAvatarSrc?: string
      showRestartButton?: boolean
      welcomeMessage?: string
      errorMessage?: string
      backgroundColor?: string
      width: number
      height: number
      fontSize?: number
      typeDelay?: number
      starterPrompts?: string[]
      starterPromptFontSize?: number
      clearChatOnReload?: boolean
      loadingMessage?: string
      botMessage?: {
        backgroundColor?: string
        textColor?: string
        showAvatar?: boolean
        avatarSrc?: string
      }
      userMessage?: {
        backgroundColor?: string
        textColor?: string
        showAvatar?: boolean
        avatarSrc?: string
      }
      textInput?: {
        placeholder?: string
        backgroundColor?: string
        textColor?: string
        sendButtonColor?: string
        maxChars?: number
        maxCharsWarningMessage?: string
        autoFocus?: boolean
      }
      footer?: {
        showFooter?: boolean
        footerText?: string
        footerTextColor?: string
        footerFontSize?: number
        footerTextAlign?: string
      }
    }
  }
  onSendMessage?: (message: string) => Promise<string>
  onRestart?: () => void
}

export const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [frameLoaded, setFrameLoaded] = useState(false)
  const [showButton, setShowButton] = useState(false)
  const [tooltipClosed, setTooltipClosed] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const chatFrameRef = useRef<HTMLIFrameElement>(null)
  const iosScrollY = useRef(0)
  const cacheIdRef = useRef(Date.now()) // Stable cache ID

  // Configuration matching the original zehnvisor-button.js exactly
  const config: ChatWidgetConfig = {
    theme: {
      button: {
        size: 40,
        backgroundColor: '#2975ba',
        iconColor: '#ffffff',
        opacity: 0.7,
        bottom: 20,
        right: 20,
        customIconSrc: '/images/chat-bubble.svg',
        customIconSize: 28,
        autoWindowOpen: {
          autoOpen: false,
          openDelay: 2
        }
      },
      tooltip: {
        showTooltip: false,
        tooltipMessage: 'Have questions? Chat with our assistant!',
        tooltipBackgroundColor: '#EFF6FF',
        tooltipTextColor: '#1E40AF',
        tooltipFontSize: 15
      },
      chatWindow: {
        chatFramePath: '/scripts/zehnvisor/chat-frame',
        showTitle: true,
        title: 'Zehnvisor - Your AI Assistant',
        titleAvatarSrc: '/images/robot.svg',
        showRestartButton: true,
        welcomeMessage: '👋 Hello! I\'m Zehnvisor. How can I help you today?',
        errorMessage: 'Oh oh! I apologize, but something went wrong. Please try again later.',
        backgroundColor: '#ffffff',
        height: 600,
        width: 800,
        fontSize: 16,
        typeDelay: 15,
        starterPrompts: [
          'What are the brokerage account requirements?',
          'What is the Month to Date return of ETF Blend 100-20?',
          'Compare ETF Blend 300-40 and ETF Blend 101-15 on execution metrics.',
          'Describe the risk profile of ETF Blend 300-40.',
          'Which strategy is right for me?',
          'How do I get started?'
        ],
        starterPromptFontSize: 14,
        clearChatOnReload: false,
        loadingMessage: 'Please wait while we get Zehnvisor on the line for you...',
        botMessage: {
          backgroundColor: '#2975ba',
          textColor: '#ffffff',
          showAvatar: true,
          avatarSrc: '/images/robot.svg'
        },
        userMessage: {
          backgroundColor: '#F3F4F6',
          textColor: '#1F2937',
          showAvatar: true,
          avatarSrc: '/images/user.svg'
        },
        textInput: {
          placeholder: 'Type your message...',
          backgroundColor: '#F9FAFB',
          textColor: '#111827',
          sendButtonColor: '#2975ba',
          maxChars: 250,
          maxCharsWarningMessage: 'Your message is too long. Please keep it under 250 characters.',
          autoFocus: true
        },
        footer: {
          showFooter: true,
          footerText: "Zehnvisor can make mistakes. Please double check all responses.",
          footerTextColor: "#6B7280",
          footerFontSize: 10,
          footerTextAlign: "right"
        }
      }
    },
    onSendMessage: async (message: string) => {
      var sessionId = sessionStorage.getItem('sessionId')

      if (!sessionId) {
        sessionId = Math.random().toString(36).substring(2, 15)
        sessionStorage.setItem('sessionId', sessionId)
      }
      
      const response = await fetch(import.meta.env.VITE_N8N_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          chatInput: message,
          sessionId 
        })
      })

      const data = await response.json()
      if (data && data.output) {
        return data.output
      } else {
        return 'Oh oh! I apologize, but something went wrong. Please try again later.'
      }
    },
    onRestart: () => {
      sessionStorage.removeItem('sessionId')
    }
  }

  useEffect(() => {
    // Show tooltip after delay if configured
    if (config.theme.tooltip?.showTooltip && !tooltipClosed && !isOpen) {
      const timer = setTimeout(() => {
        setShowTooltip(true)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [tooltipClosed, isOpen, config.theme.tooltip?.showTooltip])

  useEffect(() => {
    // Expose toggleChat globally for external access
    (window as any).ChatWidget = {
      open: () => !isOpen && toggleChat(),
      close: () => isOpen && toggleChat(),
      toggle: toggleChat,
      isOpen
    }

    return () => {
      delete (window as any).ChatWidget
    }
  }, [isOpen])

  useEffect(() => {
    // Listen for messages from iframe
    const handleMessage = (event: MessageEvent) => {
      if (!chatFrameRef.current || event.source !== chatFrameRef.current.contentWindow) {
        return
      }

      const data = event.data
      
      switch (data.type) {
        case 'frame_loaded':
          setFrameLoaded(true)
          if (isOpen) {
            sendConfigToFrame()
          }
          break
        case 'ready':
          // Chat is initialized in frame
          break
        case 'close':
          toggleChat()
          break
        case 'restarted':
          if (config.onRestart) {
            config.onRestart()
          }
          break
        case 'send_message':
          if (data.message && config.onSendMessage) {
            handleMessageCallback(data.message)
          } else {
            sendErrorToFrame()
          }
          break
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [isOpen, config])

  const sendConfigToFrame = () => {
    if (!chatFrameRef.current || !frameLoaded) return

    // Create a deep copy of the config excluding functions
    const cloneableConfig = JSON.parse(JSON.stringify(config))
    
    // Make image paths absolute
    const baseUrl = window.location.origin
    const makeAbsolute = (path: string) => {
      if (!path || path.startsWith('http') || path.startsWith('data:')) return path
      return path.startsWith('/') ? baseUrl + path : baseUrl + '/' + path
    }

    if (cloneableConfig.theme.button?.customIconSrc) {
      cloneableConfig.theme.button.customIconSrc = makeAbsolute(cloneableConfig.theme.button.customIconSrc)
    }
    if (cloneableConfig.theme.chatWindow?.titleAvatarSrc) {
      cloneableConfig.theme.chatWindow.titleAvatarSrc = makeAbsolute(cloneableConfig.theme.chatWindow.titleAvatarSrc)
    }
    if (cloneableConfig.theme.chatWindow?.botMessage?.avatarSrc) {
      cloneableConfig.theme.chatWindow.botMessage.avatarSrc = makeAbsolute(cloneableConfig.theme.chatWindow.botMessage.avatarSrc)
    }
    if (cloneableConfig.theme.chatWindow?.userMessage?.avatarSrc) {
      cloneableConfig.theme.chatWindow.userMessage.avatarSrc = makeAbsolute(cloneableConfig.theme.chatWindow.userMessage.avatarSrc)
    }

    chatFrameRef.current.contentWindow?.postMessage({
      type: 'init',
      config: cloneableConfig
    }, '*')
  }

  const handleMessageCallback = async (message: string) => {
    try {
      if (config.onSendMessage) {
        const response = await config.onSendMessage(message)
        if (response && chatFrameRef.current) {
          chatFrameRef.current.contentWindow?.postMessage({
            type: 'message_response',
            message: response
          }, '*')
        } else {
          sendErrorToFrame()
        }
      } else {
        sendErrorToFrame()
      }
    } catch (error) {
      sendErrorToFrame()
    }
  }

  const sendErrorToFrame = () => {
    if (chatFrameRef.current && frameLoaded) {
      chatFrameRef.current.contentWindow?.postMessage({
        type: 'error_response'
      }, '*')
    }
  }

  const toggleChat = () => {
    const newIsOpen = !isOpen
    setIsOpen(newIsOpen)

    if (newIsOpen) {
      // Opening chat
      const isMobile = window.innerWidth <= 768
      if (isMobile) {
        // iOS-specific body handling
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
        if (isIOS) {
          iosScrollY.current = window.scrollY
          document.body.style.position = 'fixed'
          document.body.style.width = '100%'
          document.body.style.height = '100%'
          document.body.style.top = `-${window.scrollY}px`
        } else {
          document.body.style.overflow = 'hidden'
          document.documentElement.style.overflow = 'hidden'
        }
      }

      // Initialize chat if frame is loaded
      if (frameLoaded) {
        sendConfigToFrame()
      }

      // Hide tooltip
      setShowTooltip(false)
      setTooltipClosed(true)
    } else {
      // Closing chat
      const isMobile = window.innerWidth <= 768
      if (isMobile) {
        // iOS-specific body handling
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
        if (isIOS) {
          document.body.style.position = ''
          document.body.style.width = ''
          document.body.style.height = ''
          document.body.style.top = ''
          window.scrollTo(0, iosScrollY.current)
        } else {
          document.body.style.overflow = ''
          document.documentElement.style.overflow = ''
        }
      }
    }
  }

  const handleFrameLoad = () => {
    setFrameLoaded(true)
    setShowButton(true)
    if (isOpen) {
      sendConfigToFrame()
    }
  }

  const handleRestartChat = () => {
    if (chatFrameRef.current && frameLoaded) {
      chatFrameRef.current.contentWindow?.postMessage({ type: 'restart' }, '*')
    }
  }

  const handleTooltipClick = () => {
    toggleChat()
    setShowTooltip(false)
    setTooltipClosed(true)
  }

  const closeTooltip = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowTooltip(false)
    setTooltipClosed(true)
  }

  return (
    <>
      {/* Tooltip */}
      {showTooltip && config.theme.tooltip && (
        <div css={tooltipStyles} onClick={handleTooltipClick}>
          {config.theme.tooltip.tooltipMessage}
          <button className="close-btn" onClick={closeTooltip}>✕</button>
        </div>
      )}

      {/* Chat Button */}
      {showButton && (
        <button 
          id="zehnvisor-chat-button"
          css={chatButtonStyles} 
          className={isOpen ? 'active' : ''}
          onClick={toggleChat}
        >
          {config.theme.button.customIconSrc ? (
            <img 
              src={config.theme.button.customIconSrc} 
              alt="Chat" 
              style={{ 
                width: config.theme.button.customIconSize || config.theme.button.size * 0.6,
                height: config.theme.button.customIconSize || config.theme.button.size * 0.6
              }}
            />
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          )}
        </button>
      )}

      {/* Chat Container */}
      <div css={chatContainerStyles} className={isOpen ? 'open' : ''}>
        {/* Chat Header */}
        <div css={chatHeaderStyles}>
          <div css={headerTitleStyles}>
            <img 
              src={config.theme.chatWindow.titleAvatarSrc} 
              alt="Bot" 
            />
            <span>{config.theme.chatWindow.title}</span>
          </div>
          <div css={headerButtonsStyles}>
            {config.theme.chatWindow.showRestartButton !== false && (
              <button title="Restart chat" onClick={handleRestartChat}>
                <i className="fas fa-rotate-left"></i>
              </button>
            )}
            <button title="Close chat" onClick={toggleChat}>
              <i className="fas fa-xmark"></i>
            </button>
          </div>
        </div>

        {/* Loading Container */}
        {!frameLoaded && (
          <div css={loadingContainerStyles}>
            <div className="loading-spinner"></div>
            <p>{config.theme.chatWindow.loadingMessage}</p>
          </div>
        )}

        {/* Chat Frame */}
        <iframe
          ref={chatFrameRef}
          css={chatFrameStyles}
          className={frameLoaded ? 'loaded' : ''}
          src={`${config.theme.chatWindow.chatFramePath}?cb=${cacheIdRef.current}`}
          title="Chat with Zehnvisor"
          frameBorder="0"
          onLoad={handleFrameLoad}
        />
      </div>
    </>
  )
}